/**
 * API Endpoints Constants
 *
 * Centralized API endpoint definitions matching the .NET Core backend.
 * All endpoints are relative to the base URL configured in axios.
 */

export const API_ENDPOINTS = {
    // Authentication
    AUTH: {
        LOGIN: '/auth/login',
        REGISTER: '/auth/register',
        FORGOT_PASSWORD: '/auth/forgot-password',
        RESET_PASSWORD: '/auth/reset-password',
        REFRESH_TOKEN: '/auth/refresh-token',
        LOGOUT: '/auth/logout',
    },

    // Admin Authentication
    ADMIN_AUTH: {
        LOGIN: '/admin/auth/login',
        PROFILE: '/admin/auth/me',
        CHANGE_PASSWORD: '/admin/auth/change-password',
        REFRESH_TOKEN: '/admin/auth/refresh-token',
        LOGOUT: '/admin/auth/logout',
    },

    // Users
    USERS: {
        BASE: '/user',
        PROFILE: '/user/profile',
        UPDATE_PROFILE: '/user/profile',
        CHANGE_PASSWORD: '/user/change-password',
        ORDERS: '/user/orders',
        ADDRESSES: '/user/addresses',
        WISHLIST: '/user/wishlist',
    },

    // Products
    PRODUCTS: {
        BASE: '/product',
        BY_ID: (id: number) => `/product/${id}`,
        BY_CATEGORY: (categoryId: number) => `/product/category/${categoryId}`,
        BY_BRAND: (brandId: number) => `/product/brand/${brandId}`,
        SEARCH: '/product/search',
        FEATURED: '/product/featured',
    },

    // Categories
    CATEGORIES: {
        BASE: '/category',
        BY_ID: (id: number) => `/category/${id}`,
        WITH_SUBCATEGORIES: '/category/with-subcategories',
    },

    // Sub-Categories
    SUB_CATEGORIES: {
        BASE: '/subcategories',
        BY_ID: (id: number) => `/subcategories/${id}`,
        BY_CATEGORY: (categoryId: number) => `/subcategories/category/${categoryId}`,
    },

    // Brands
    BRANDS: {
        BASE: '/brands',
        BY_ID: (id: number) => `/brands/${id}`,
    },

    // Cart
    CART: {
        BASE: '/cart',
        ADD: '/cart/add',
        UPDATE: '/cart/update',
        REMOVE: (productId: number) => `/cart/remove/${productId}`,
        CLEAR: '/cart/clear',
    },

    // Orders
    ORDERS: {
        BASE: '/orders',
        BY_ID: (id: number) => `/orders/${id}`,
        CREATE: '/orders',
        USER_ORDERS: '/orders/user',
        CHECKOUT_SETTINGS: '/orders/checkout-settings',
    },

    // Shipping (live carrier rate quotes for checkout)
    SHIPPING: {
        RATES: '/shipping/rates',
    },

    // Admin Orders
    ADMIN_ORDERS: {
        BASE: '/admin-orders',
        BY_ID: (id: number) => `/admin-orders/${id}`,
        UPDATE_STATUS: (id: number) => `/admin-orders/${id}/status`,
        ADD_NOTE: (id: number) => `/admin-orders/${id}/notes`,
    },

    // Sliders
    SLIDERS: {
        BASE: '/sliders',
        BY_ID: (id: number) => `/sliders/${id}`,
        ACTIVE: '/sliders/active',
    },

    // Reviews
    REVIEWS: {
        BASE: '/reviews',
        BY_PRODUCT: (productId: number) => `/reviews/product/${productId}`,
        CREATE: '/reviews',
    },

    // Bespoke Services
    BESPOKE_SERVICES: {
        BASE: '/bespoke-services',
        BY_ID: (id: number) => `/bespoke-services/${id}`,
        BY_SLUG: (slug: string) => `/bespoke-services/slug/${slug}`,
    },

    // About Us
    ABOUT_US: {
        BASE: '/about-us',
        UPDATE: '/about-us',
    },

    // Coupons
    COUPONS: {
        BASE: '/coupons',
        VALIDATE: '/coupons/validate',
        APPLY: '/coupons/apply',
    },

    // Payments
    PAYMENTS: {
        CREATE_INTENT: '/payments/create-intent',
        CONFIRM: '/payments/confirm',
    },

    // File Upload
    FILES: {
        UPLOAD: '/fileupload',
        BY_ID: (id: string) => `/files/${id}`,
    },

    // Inventory
    INVENTORY: {
        BASE: '/inventory',
        BY_PRODUCT: (productId: number) => `/inventory/product/${productId}`,
        UPDATE_STOCK: (productId: number) => `/inventory/product/${productId}/stock`,
    },

    // Admin Users
    ADMIN_USERS: {
        BASE: '/admin-users',
        BY_ID: (id: number) => `/admin-users/${id}`,
    },

    // Sales & Promotions
    SALES: {
        BASE: '/sales',
        BY_ID: (id: string) => `/sales/${id}`,
        ACTIVE: '/sales/active',
        CALCULATE_DISCOUNT: '/sales/calculate',
    },

    // Carriers
    CARRIERS: {
        BASE: '/carriers',
        BY_ID: (id: string) => `/carriers/${id}`,
    },

    // Error Logs
    ERROR_LOGS: {
        BASE: '/error-logs',
        BY_ID: (id: number) => `/error-logs/${id}`,
    },

    // Site Settings
    SITE_SETTINGS: {
        BASE: '/sitesettings',
    },

    // Contact Us
    CONTACT_US: {
        BASE: '/contact-us',
        SEND: '/contact-us/send',
    },
} as const;

export type ApiEndpoints = typeof API_ENDPOINTS;
